import React from "react";
import { toast } from "sonner";
import { Copy, Users as UsersIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Agency-tier referral card. Encapsulates the unique-link block, the Copy
 * button (uses navigator.clipboard + sonner.toast), and the signup count.
 * Renders nothing if no referral payload is supplied.
 *
 * The empty-state and report-state dashboards both mount this component;
 * the `variant` prop tweaks the outer wrapper margins so it fits cleanly
 * inside either layout.
 */
export default function ReferralSection({ referral, variant = "report" }) {
  if (!referral) return null;

  const url = `${window.location.origin}${referral.share_url_path}`;
  const copy = () => {
    try {
      navigator.clipboard.writeText(url);
      toast.success("Referral link copied — share with colleagues");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const outerCls =
    variant === "empty"
      ? "mt-8 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 text-left"
      : "mt-6 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5";

  return (
    <div className={outerCls} data-testid="referral-section">
      <div className="flex items-start gap-4">
        <div className="rounded-full bg-[var(--rg-sage)] text-[var(--rg-text-strong)] w-10 h-10 flex items-center justify-center flex-shrink-0">
          <UsersIcon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-sage)] mb-1">
            Refer a colleague
          </div>
          <div className="text-[var(--rg-text-strong)] text-sm mb-2">
            Share your unique referral link. When a new user signs up they receive{" "}
            <span className="text-[var(--rg-sage)] font-semibold">30 days of free Growth access</span>.
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <code
              className="text-xs font-mono bg-[var(--rg-bg)] border border-[var(--rg-border)] px-3 py-2 rounded text-[var(--rg-text)]"
              data-testid="referral-link"
            >
              {url}
            </code>
            <Button
              size="sm"
              onClick={copy}
              className="bg-[var(--rg-sage)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-sage)] font-semibold"
              data-testid="copy-referral-btn"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
            </Button>
          </div>
          <div className="text-xs text-[var(--rg-muted)]" data-testid="referral-count">
            {referral.signups_count} signup{referral.signups_count === 1 ? "" : "s"} so far.
          </div>
        </div>
      </div>
    </div>
  );
}
